import FilledButtonM from "@common/components/button/FilledButtonM"
import IconButtonM from "@common/components/button/IconButtonM"
import AnswerCardM from "@common/components/funnel/AnswerCardM"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { HeaderContainerM } from "@common/header/HeaderContainerM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { apiRequest } from "@data/redux/actions/api"
import { tutorTrainingService } from "@data/services/tutorTrainingService"
import { useNavigation } from "@react-navigation/native"
import React, { useContext, useEffect, useRef, useState } from "react"
import { ScrollView, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import { TrainingVideoPlayer } from "./TrainingVideoPlayer"

const GUTTER = 20

const PHASE_VIDEO = "video"
const PHASE_QUIZ = "quiz"
const PHASE_RESULT = "result"

/**
 * « Ta formation » — the training video, then the validation quiz, then the
 * result. One screen, three phases, all fed by the `tutorTraining` reducer.
 *
 * A retake re-fetches the questions: the server shuffles the choices on every
 * read, so the second attempt is not the first one with the same positions.
 */
const TutorTrainingScreen = (props) => {
    const navigation = useNavigation()
    const dimensions = useContext(DimensionsContext)
    const scrollRef = useRef(null)

    const [phase, setPhase] = useState(PHASE_VIDEO)
    const [answers, setAnswers] = useState({})

    const { step, questions, progress, lastResult, passThresholdPercent } =
        props.training

    useEffect(() => {
        props.apiRequest(tutorTrainingService.getTutorTraining)
    }, [])

    useApiStatus(
        tutorTrainingService.submitTutorTrainingQuiz,
        null,
        true,
        () => {
            setPhase(PHASE_RESULT)
            scrollRef.current?.scrollTo?.({ y: 0, animated: false })
        }
    )

    const goBack = () => {
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu")
    }

    const startQuiz = () => {
        setAnswers({})
        setPhase(PHASE_QUIZ)
        scrollRef.current?.scrollTo?.({ y: 0, animated: false })
    }

    const retakeQuiz = () => {
        props.apiRequest(tutorTrainingService.getTutorTraining)
        startQuiz()
    }

    const submit = () =>
        props.apiRequest(
            tutorTrainingService.submitTutorTrainingQuiz,
            { id: step.id },
            {
                step_answers: Object.fromEntries(
                    Object.entries(answers).map(([id, answer]) => [
                        id,
                        [answer],
                    ])
                ),
            }
        )

    const width = dimensions?.width - 2 * GUTTER
    const answeredCount = questions.filter((q) => answers[q.id]).length
    const neededCount = Math.ceil(
        (questions.length * passThresholdPercent) / 100
    )

    return (
        <>
            <HeaderContainerM style={{ justifyContent: "flex-start" }}>
                <IconButtonM
                    icon="arrow-left"
                    iconColor={theme.colors.primary}
                    size={30}
                    onPress={goBack}
                />
                <TitleM>Ta formation</TitleM>
            </HeaderContainerM>

            <ScreenSkeleton
                isLoading={!step && !props.loadError}
                errorScreenProps={{
                    error: step ? null : props.loadError,
                    title: "Impossible de charger ta formation",
                    onButtonPress: () =>
                        props.apiRequest(tutorTrainingService.getTutorTraining),
                }}
            >
                <ScrollView
                    ref={scrollRef}
                    style={{ width: dimensions?.width, alignSelf: "center" }}
                    contentContainerStyle={{
                        paddingHorizontal: GUTTER,
                        paddingTop: 20,
                        paddingBottom: 40,
                    }}
                >
                    {phase === PHASE_VIDEO && step && (
                        <VideoPhase
                            step={step}
                            progress={progress}
                            questionsCount={questions.length}
                            width={width}
                            onStartQuiz={startQuiz}
                        />
                    )}

                    {phase === PHASE_QUIZ && (
                        <>
                            <TextM wrap style={{ marginBottom: 16 }}>
                                {`Une seule bonne réponse par question. Il en faut ${neededCount} sur ${questions.length} pour valider, et tu peux repasser le quiz autant de fois que tu veux.`}
                            </TextM>
                            {questions.map((question, index) => (
                                <QuestionCard
                                    key={question.id}
                                    index={index}
                                    question={question}
                                    selected={answers[question.id]}
                                    onSelect={(answer) =>
                                        setAnswers({
                                            ...answers,
                                            [question.id]: answer,
                                        })
                                    }
                                />
                            ))}
                            <TextM
                                center
                                style={{ marginTop: 8, marginBottom: 12 }}
                            >
                                {`${answeredCount} / ${questions.length} réponses`}
                            </TextM>
                            <FilledButtonM
                                color={theme.colors.primary}
                                label="Valider mes réponses"
                                disabled={answeredCount < questions.length}
                                loading={props.submitting}
                                onPress={submit}
                            />
                            <FilledButtonM
                                mode="text"
                                color={theme.colors.link}
                                label="Revoir la vidéo"
                                onPress={() => setPhase(PHASE_VIDEO)}
                            />
                        </>
                    )}

                    {phase === PHASE_RESULT && lastResult && (
                        <ResultPhase
                            result={lastResult}
                            questions={questions}
                            neededCount={neededCount}
                            onRetake={retakeQuiz}
                            onReviewVideo={() => setPhase(PHASE_VIDEO)}
                            onDone={() => navigation.navigate("menu")}
                        />
                    )}
                </ScrollView>
            </ScreenSkeleton>
        </>
    )
}

function VideoPhase({ step, progress, questionsCount, width, onStartQuiz }) {
    const passed = progress?.status === "passed"

    return (
        <>
            <TextM
                fontWeight="Bold"
                fontSize={20}
                wrap
                color={theme.colors.primary}
            >
                {step.title}
            </TextM>
            <TextM wrap style={{ marginTop: 8, marginBottom: 16 }}>
                {/* No duration here: the player shows its own, at its own speed. */}
                {step.description}
            </TextM>

            <TrainingVideoPlayer
                embedUrl={step.embed_url}
                videoUrl={step.video_url}
                width={width}
            />

            <View
                style={{
                    marginTop: 24,
                    padding: 16,
                    borderRadius: 12,
                    backgroundColor: passed
                        ? theme.colors.successLight
                        : theme.colors.primaryLighter,
                }}
            >
                <TextM fontWeight="Bold" wrap color={theme.colors.primary}>
                    {passed
                        ? "Tu as validé ta formation"
                        : "Ensuite, un petit quiz"}
                </TextM>
                <TextM wrap style={{ marginTop: 6 }}>
                    {passed
                        ? "Tu peux revoir la vidéo quand tu veux, et repasser le quiz pour te tester."
                        : `${questionsCount} questions pour vérifier que tout est clair avant ton premier cours. Prends le temps de regarder la vidéo en entier, les réponses y sont.`}
                </TextM>
            </View>

            <FilledButtonM
                color={theme.colors.primary}
                label={passed ? "Repasser le quiz" : "Passer le quiz"}
                style={{ marginTop: 20 }}
                onPress={onStartQuiz}
            />
        </>
    )
}

function QuestionCard({ index, question, selected, onSelect }) {
    return (
        <View
            style={{
                marginBottom: 16,
                padding: 16,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: theme.colors.lightMore,
                backgroundColor: theme.colors.white,
            }}
        >
            <TextM fontWeight="Bold" wrap color={theme.colors.primary}>
                {`${index + 1}. ${question.question_string}`}
            </TextM>
            {question.answers.map((answer) => (
                <AnswerCardM
                    key={answer}
                    label={answer}
                    selected={selected === answer}
                    onPress={() => onSelect(answer)}
                    style={{ marginTop: 10 }}
                />
            ))}
        </View>
    )
}

function ResultPhase({
    result,
    questions,
    neededCount,
    onRetake,
    onReviewVideo,
    onDone,
}) {
    const questionById = Object.fromEntries(questions.map((q) => [q.id, q]))

    if (result.passed) {
        return (
            <View style={{ alignItems: "center" }}>
                <FontAwesome5
                    name="check-circle"
                    size={48}
                    color={theme.colors.success}
                    solid
                />
                <TextM
                    fontWeight="ExtraBold"
                    fontSize={24}
                    color={theme.colors.primary}
                    center
                    style={{ marginTop: 16 }}
                >
                    Tu es prêt·e !
                </TextM>
                <TextM
                    fontWeight="Bold"
                    fontSize={18}
                    center
                    style={{ marginTop: 8 }}
                >
                    {`${result.correct_count} / ${result.total}`}
                </TextM>
                <TextM wrap center style={{ marginTop: 16, marginBottom: 24 }}>
                    Bravo, ta formation est validée. Dernière étape avant ton
                    premier cours : une courte session avec l'équipe Tutor
                    Excellence, pour vérifier ensemble que tout est en place.
                    Elle te contacte pour la caler.
                </TextM>
                <FilledButtonM
                    color={theme.colors.primary}
                    label="Retour à l'accueil"
                    onPress={onDone}
                />
            </View>
        )
    }

    return (
        <>
            <TextM
                fontWeight="ExtraBold"
                fontSize={22}
                color={theme.colors.primary}
                center
            >
                {`${result.correct_count} / ${result.total}`}
            </TextM>
            <TextM wrap center style={{ marginTop: 8, marginBottom: 20 }}>
                {`Presque ! Il faut ${neededCount} bonnes réponses pour valider. Voici les bonnes réponses, relis-les puis retente ta chance.`}
            </TextM>

            <FilledButtonM
                color={theme.colors.primary}
                label="Repasser le quiz"
                onPress={onRetake}
            />
            <FilledButtonM
                mode="text"
                color={theme.colors.link}
                label="Revoir la vidéo"
                onPress={onReviewVideo}
            />

            {/* The missed questions first, each keeping its number in the quiz. */}
            {result.results
                .map((row, index) => ({ row, index }))
                .sort((a, b) => a.row.correct - b.row.correct)
                .map(({ row, index }) => (
                    <CorrectionRow
                        key={row.question_id}
                        index={index}
                        row={row}
                        question={questionById[row.question_id]}
                    />
                ))}
        </>
    )
}

function CorrectionRow({ index, row, question }) {
    return (
        <View
            style={{
                marginTop: 12,
                padding: 14,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: row.correct
                    ? theme.colors.lightMore
                    : theme.colors.errorLight,
                backgroundColor: theme.colors.white,
            }}
        >
            <View style={{ flexDirection: "row", alignItems: "flex-start" }}>
                <FontAwesome5
                    name={row.correct ? "check-circle" : "times-circle"}
                    size={16}
                    color={
                        row.correct ? theme.colors.success : theme.colors.error
                    }
                    solid
                    style={{ marginRight: 8, marginTop: 2 }}
                />
                <TextM fontWeight="Bold" wrap style={{ flex: 1 }}>
                    {`${index + 1}. ${question?.question_string ?? ""}`}
                </TextM>
            </View>
            {!row.correct && (
                <>
                    <TextM
                        wrap
                        style={{ marginTop: 8 }}
                        color={theme.colors.error}
                    >
                        {`Ta réponse : ${row.tutor_answers.join(", ")}`}
                    </TextM>
                    <TextM
                        wrap
                        style={{ marginTop: 4 }}
                        color={theme.colors.success}
                    >
                        {`Bonne réponse : ${row.valid_answers.join(", ")}`}
                    </TextM>
                </>
            )}
        </View>
    )
}

const mapStateToProps = (state) => ({
    training: state.data.tutorTraining,
    loadError: state.api.getTutorTraining?.error,
    submitting: state.api.submitTutorTrainingQuiz?.request,
})

const mapDispatchToProps = { apiRequest }

export default connect(mapStateToProps, mapDispatchToProps)(TutorTrainingScreen)
